import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { readServerBuild } from "../../api/serverBuildApi";
import { formatDateTime } from "../../shared/formatting/dateTimes";
import { useLoad } from "../../shared/loading/useLoad";
import { shortReference } from "../../shared/references/shortReferences";
import TopbarControl from "../../ui/buttons/TopbarControl";
import MenuButton, { type MenuSection } from "../../ui/overlays/MenuButton";
import { useIssueReport } from "../issueReports/issueReportContext";
import { HELP_ARTICLES, HELP_GLOSSARY, HELP_START, type HelpArticleId } from "./helpArticles";
import { useHelpOpener } from "./opening/helpOpening";

/** The "?" in the top bar, left of the account menu (web-app.md WA-6): help on this page, the
 * guide and the glossary, opened as Personal preferences say, then "Report a problem…" (F13).
 * Its last line names the deployed commit and when that server started (F160). `article` is the
 * help of the page worked in, which the shell reads from its route. */
export default function HelpMenu({ className, article }: { className?: string; article: HelpArticleId | null }) {
  const { t } = useTranslation();
  const openIssueReport = useIssueReport();
  const { open } = useHelpOpener();
  const sections: MenuSection[] = [
    {
      id: "help",
      items: [
        ...(article === null ? [] : [{ id: "page", label: t("help.thisPage"), action: { kind: "select", onSelect: () => { open(HELP_ARTICLES[article]); } } } as const]),
        { id: "guide", label: t("help.guide"), action: { kind: "select", onSelect: () => { open(HELP_START); } } },
        { id: "glossary", label: t("help.glossary"), action: { kind: "select", onSelect: () => { open(HELP_GLOSSARY); } } },
      ],
    },
    {
      id: "report",
      items: openIssueReport === null ? [] : [{ id: "report", label: t("issueReports.menuItem"), action: { kind: "select", onSelect: () => { openIssueReport(); } } }],
    },
  ];
  return (
    <MenuButton
      className={className}
      align="right"
      width="md"
      sections={sections}
      footer={<BuildLine />}
      renderTrigger={(trigger) => (
        <TopbarControl {...trigger} className="px-2" aria-label={t("help.open")} title={t("help.open")}>
          <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden="true" />
        </TopbarControl>
      )}
    />
  );
}

/** The deploy this server runs, read each time Help opens (the menu mounts its footer only while
 * open), so a deploy or restart since the page loaded shows. A server run from a checkout names
 * no commit: then there is no line. */
function BuildLine() {
  const { t, i18n } = useTranslation();
  const { state: build } = useLoad(readServerBuild);
  if (build.status !== "loaded" || build.value.commit === null) return null;
  const { commit, startedAt } = build.value;
  return (
    <>
      <div aria-hidden="true" className="app-menu-separator" />
      <p className="app-text-muted px-3 py-2 text-xs" title={commit}>
        {t("help.build", { commit: shortReference("gitObject", commit), started: formatDateTime(startedAt, i18n.resolvedLanguage) })}
      </p>
    </>
  );
}
